// utils.ts - Utility functions and helpers
import {
  PurchaseRequest,
  AssetRequest,
  RevaluationRequest,
  SellRequest,
  RequestDisplayData
} from './types';

export const formatCurrency = (amount: string | number): string => {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(numAmount);
};

export const formatUnits = (units: string | number): string => {
  const numUnits = typeof units === 'string' ? parseFloat(units) : units;
  return numUnits.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
};

export const formatDateTime = (dateString: string | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return dateString;
  }
};

export const getStatusConfig = (status: string) => {
  switch (status) {
    case 'pending':
      return { color: '#ff9800', bgColor: '#fff3e0', label: 'Pending' };
    case 'accepted':
    case 'approved':
      return { color: '#4caf50', bgColor: '#e8f5e8', label: 'Accepted' };
    case 'rejected':
      return { color: '#f44336', bgColor: '#ffebee', label: 'Rejected' };
    default:
      return { color: '#757575', bgColor: '#f5f5f5', label: 'Unknown' };
  }
};

export const isAdminLogin = (locationState?: { isAdmin?: boolean }): boolean => {
  console.log('Checking admin login with locationState:', locationState?.isAdmin);
  const loginInfo = localStorage.getItem('user-details');
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}') as {
    projectId?: string;
    projectName?: string;
    isAdmin?: boolean;
  };

  let userHasAdminRole = false;

  if (loginInfo) {
    try {
      const userDetails = JSON.parse(loginInfo);
      userHasAdminRole =
        selectedProject?.isAdmin === true ||
        userDetails?.role?.some((r: any) => r.label?.toLowerCase() === 'admin');
    } catch (error) {
      console.error('Error parsing user-details:', error);
      return false;
    }
  }

  if (!userHasAdminRole) {
    return false;
  }

  return locationState?.isAdmin ?? false;
};

// ✅ UPDATED: Handle SellRequest in getRequestDisplayData
export const getRequestDisplayData = (
  request: PurchaseRequest | AssetRequest | RevaluationRequest | SellRequest
): RequestDisplayData => {
  // ✅ Check for SellRequest first (has sellUnits and operationCharge)
  if ('sellUnits' in request && 'operationCharge' in request) {
    const totalAmount = parseFloat(request.sellUnits) * parseFloat(request.unitPrice);
    return {
      title: request.investorName,
      subtitle: 'Unit Sell Request',
      avatar: request.investorAvatar,
      email: request.investorEmailId,
      amount: totalAmount.toString(),
      units: request.sellUnits,
      createdAt: request.createdAt,
      lastUpdatedDateTime: request.lastUpdatedDateTime
    };
  }
  // Check for PurchaseRequest
  else if ('investorName' in request && 'investedAmount' in request) {
    return {
      title: request.investorName,
      subtitle: 'Income Units',
      avatar: request.investorAvatar,
      email: request.investorEmailId,
      amount: request.investedAmount,
      units: request.investedUnits,
      createdAt: request.createdAt,
      lastUpdatedDateTime: request.lastUpdatedDateTime
    };
  }
  // Check for RevaluationRequest
  else if ('asset' in request && 'assetData' in request.asset) {
    return {
      title: request.asset.assetData.identifier,
      subtitle: 'Asset Revaluation',
      avatar: undefined,
      email: request.asset.fundManager,
      amount: request.asset.assetData.purchaseValue,
      units: request.asset.cashUsed,
      createdAt: request.createdDate,
      lastUpdatedDateTime: request.lastUpdatedDate
    };
  }
  // AssetRequest
  else {
    return {
      title: request.asset.identifier || '',
      subtitle: 'Asset Purchase',
      avatar: undefined,
      email: request.fundManager || '',
      amount: request.asset.purchaseValue || '',
      units: request.asset.cashUsed,
      createdAt: request.createdAt,
      lastUpdatedDateTime: request.lastUpdatedDateTime
    };
  }
};

export const filterRequests = (
  requests: (PurchaseRequest | AssetRequest | RevaluationRequest | SellRequest)[],
  filterStatus: string,
  searchTerm: string
) => {
  return requests.filter((request) => {
    const statusMatch = filterStatus === 'all' || request.status === filterStatus;
    let searchMatch = !searchTerm;

    if (!searchMatch) {
      if ('investorName' in request) {
        searchMatch =
          request.investorName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          request.investorEmailId.toLowerCase().includes(searchTerm.toLowerCase());
      } else if ('asset' in request) {
        const asset = 'assetData' in request.asset ? request.asset.assetData : request.asset;
        searchMatch =
          asset.identifier.toLowerCase().includes(searchTerm.toLowerCase()) ||
          asset.description.toLowerCase().includes(searchTerm.toLowerCase());
      }
    }

    return statusMatch && searchMatch;
  });
};

export const getUserDetails = () => {
  const loginInfo = localStorage.getItem('user-details');
  return loginInfo ? JSON.parse(loginInfo) : null;
};

export const getInvestorEmail = (): string => {
  const userDetails = getUserDetails();
  return userDetails?.email || '';
};
